@if (confirmModal().isOpen) {
  <app-modal
    [config]="confirmModal().config"
    (closeModal)="closeConfirmModal()"
    [title]="confirmModal().titleKey | translate">
    <div modal-body>
      {{ confirmModal().descriptionKey | translate }}
    </div>
  </app-modal>
}

<div class="flex h-full w-full flex-col gap-1.5 overflow-hidden">
  <div class="border-default bg-elevated flex w-full flex-col gap-4 rounded-lg border">
    <!-- Header de l'accordéon -->
    <button
      type="button"
      (click)="toggleFormExpansion()"
      class="hover:bg-interactive flex w-full cursor-pointer items-center justify-between rounded-lg p-4 text-left transition-colors">
      <span class="text-text-primary flex flex-row">
        {{ 'job_assisted_importation.parameters.title' | translate }}
        @if (selectedDateInLocale(); as date) {
          <div class="text-text-primary ml-1 font-normal">: {{ date }}</div>
        }
      </span>
      <svg
        [class.rotate-180]="!isFormExpanded()"
        class="text-text-tertiary duration-base size-5 transition-transform"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    @if (isFormExpanded()) {
      <div class="px-4 pb-4">
        <app-form-builder
          class="w-full"
          [schema]="schema()"
          [form]="form"
          formMode="create" />
      </div>

      <div class="flex justify-end px-4 pb-4">
        <button
          type="button"
          [disabled]="!isFormValid()"
          (click)="handleGenerationClick()"
          class="inline-flex items-center rounded-md p-2 text-sm font-medium"
          [class]="' ' + (isFormValid() ? 'btn-primary' : 'btn-disabled')">
          {{ 'job_assisted_importation.parameters.generate_button' | translate }}
        </button>
      </div>
    }
  </div>
  <app-toolbox
    [sections]="toolboxSections()"
    [searchConfig]="toolboxSearchConfig()"
    [useAdvancedFiltering]="true"
    [(showAdvancedFiltering)]="showAdvancedFiltering"
    [openFilterForField]="openFilterForField()"
    [showCustomContent]="activeGrammar() === 'path'"
    (advancedQuerySearch)="onAdvancedQuerySearch($event)">
    @if (activeGrammar() === 'path' && pathBar.vocabulary(); as vocab) {
      <app-path-filter-search-bar
        class="min-w-80 flex-1"
        [vocabulary]="vocab"
        [state]="pathBar.bar()"
        (stateChange)="pathBar.editBar($event)" />
    }
  </app-toolbox>
  @if (
    activeGrammar() === 'path' && showAdvancedFiltering() && pathBar.vocabulary();
    as vocab
  ) {
    <app-path-filter-pills
      class="border-weak bg-elevated block shrink-0 border-b"
      [vocabulary]="vocab"
      [draft]="pathBar.draft()"
      (draftChange)="pathBar.editDraft($event)"
      (openAdvanced)="pathBar.pathPanel.set('filter')" />
  } @else if (showAdvancedFiltering()) {
    <app-filter-query-builder
      [availableFields]="availableAdvancedFilters()"
      [initialConfiguration]="advancedFilters()"
      [openFilterForField]="openFilterForField()"
      (filterChange)="onFilterChanges($event)" />
  }
  <div class="flex h-full min-h-0 gap-1.5">
    <div class="flex h-full min-h-0 min-w-0 flex-1 flex-col justify-between gap-1.5">
      @if (rows().length === 0) {
        <div
          class="flex h-full w-full flex-col items-center justify-center gap-4 rounded-lg border-2 border-zinc-300 bg-zinc-50 p-6 text-center dark:border-zinc-600 dark:bg-zinc-900">
          <svg
            class="dark:text-text-tertiary h-12 w-12 text-zinc-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7M3 7l9 6 9-6M3 7l9-6 9 6" />
          </svg>
          <p class="text-text-tertiary dark:text-zinc-400">
            {{ 'job_assisted_importation.no_data' | translate }}
          </p>
        </div>
      } @else if (filteredAndSortedRows().length === 0) {
        <div
          class="flex h-full w-full flex-col items-center justify-center gap-4 rounded-lg border-2 border-zinc-300 bg-zinc-50 p-6 text-center dark:border-zinc-600 dark:bg-zinc-900">
          <svg
            class="dark:text-text-tertiary h-12 w-12 text-zinc-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p class="text-text-tertiary dark:text-zinc-400">
            Aucun résultat trouvé pour votre recherche
          </p>
        </div>
      } @else {
        @defer {
          <app-table
            [data]="filteredAndSortedRows()"
            tableId="job-helped-importation-table"
            [allColumns]="tableColumns()"
            [errors]="errors()"
            [cellOptions]="cellOptions()"
            [selected]="selectedRowsSet()"
            [summaryItems]="summaryItems()"
            (addFilterOn)="addFilterOn($event)"
            (removeFilterOn)="removeFilterOn($event)"
            (indexMap)="reIndexErrors($event)"
            (rowEdit)="handleRowEdition($event)"
            [initialSort]="tableInitialSort()"
            (sortChange)="handleSortChange($event)"
            [config]="tableConfig"
            class="rounded-lg" />
        } @placeholder {
          <div
            class="flex h-full w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div class="flex gap-4 border-b border-gray-200 bg-gray-50 p-4">
              <ngx-skeleton-loader
                count="4"
                appearance="line"
                [theme]="{ width: '25%', height: '15px', 'margin-bottom': '0' }" />
            </div>
            <div class="flex flex-col gap-3 p-4">
              <ngx-skeleton-loader
                count="10"
                appearance="line"
                [theme]="{ width: '100%', height: '35px', 'margin-bottom': '15px' }" />
            </div>
          </div>
        }
      }

      @if (errors().length > 0) {
        <div class="w-full grow"></div>

        <app-errors-list class="flex max-h-72" [errors]="errors()" />
      }
    </div>
    @if (
      activeGrammar() === 'path' && pathBar.pathPanel() && pathBar.vocabulary();
      as vocab
    ) {
      <app-path-filter-panel
        appFocusFirst
        appReturnFocus
        class="h-full shrink-0"
        [title]="'features.data.path_filter.panel.title' | translate"
        [entityType]="vocab.entityType"
        [count]="filteredAndSortedRows().length"
        (closed)="pathBar.pathPanel.set(null)">
        <app-path-filter-composer
          panel-body
          [vocabulary]="vocab"
          [draft]="pathBar.draft()"
          (draftChange)="pathBar.editDraft($event)" />
        <div panel-footer class="flex flex-1 items-center gap-2">
          <i [class]="ICONS.checkCircle"></i>
          <span>{{ 'features.data.path_filter.panel.applied' | translate }}</span>
          <span class="flex-1"></span>
          <button type="button" class="btn-cancel btn-sm" (click)="pathBar.clearByHand()">
            {{ 'common.clear' | translate }}
          </button>
        </div>
      </app-path-filter-panel>
    }
  </div>
</div>
